import React, { useMemo, useState } from 'react';
import { 
  BarChart3, DollarSign, TrendingUp, ArrowRight, SlidersHorizontal, ArrowUpRight, ArrowDown, Info
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

// Demo page: Channel Mix & Budget Reallocation (6-month dummy data)
// Design aligns with existing analytics pages (cards, borders, spacing).

const CHANNELS = [
  { id: 'google', name: 'Google Ads', color: '#3b82f6', a: 180000, b: 0.0028 },
  { id: 'meta', name: 'Meta Ads', color: '#22c55e', a: 140000, b: 0.0036 },
  { id: 'tiktok', name: 'TikTok Ads', color: '#ef4444', a: 90000, b: 0.0042 },
  { id: 'influencer', name: 'Influencer', color: '#a855f7', a: 60000, b: 0.0020 },
  { id: 'email', name: 'Email', color: '#f59e0b', a: 30000, b: 0.0060 },
];

const MONTHS = (() => {
  const now = new Date();
  const arr = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
  }
  return arr;
})();

function seedRand(seed) {
  let t = seed;
  return () => {
    t ^= t << 13; t ^= t >> 17; t ^= t << 5; // xorshift
    return Math.abs(t) / 0x7fffffff;
  };
}

function buildDummyData() {
  const rnd = seedRand(123456);
  const byChannel = {};
  CHANNELS.forEach(ch => {
    const rows = [];
    let prevSpend = 30000 + rnd() * 20000; // start around 30–50k
    for (let i = 0; i < MONTHS.length; i++) {
      // random walk on spend
      const shock = (rnd() - 0.5) * 8000;
      const spend = Math.max(8000, prevSpend + shock);
      // response function: revenue = a * (1 - exp(-b * spend)) plus small noise
      const base = ch.a * (1 - Math.exp(-ch.b * spend));
      const noise = (rnd() - 0.5) * 0.07 * base;
      const revenue = Math.max(0, base + noise);
      rows.push({ month: MONTHS[i], spend: Math.round(spend), revenue: Math.round(revenue) });
      prevSpend = spend;
    }
    byChannel[ch.id] = { ...ch, rows };
  });
  return byChannel;
}

function buildBaselineDummy() {
  // Organic & Direct behave as baseline (non-allocatable) revenue sources
  const rnd = seedRand(98765);
  const amplitude = 0.08; // 8% seasonal swing
  const baseOrganic = 120000;
  const baseDirect = 90000;
  const organicRows = [];
  const directRows = [];
  for (let i = 0; i < MONTHS.length; i++) {
    const season = 1 + amplitude * Math.sin((2 * Math.PI * i) / 6);
    const noiseO = 1 + (rnd() - 0.5) * 0.06;
    const noiseD = 1 + (rnd() - 0.5) * 0.06;
    const org = Math.max(0, baseOrganic * season * noiseO);
    const dir = Math.max(0, baseDirect * (1 - amplitude / 2 + (amplitude / 2) * Math.cos((2 * Math.PI * i) / 6)) * noiseD);
    organicRows.push({ month: MONTHS[i], revenue: Math.round(org) });
    directRows.push({ month: MONTHS[i], revenue: Math.round(dir) });
  }
  return {
    organic: { id: 'organic', name: 'Organic', color: '#06b6d4', rows: organicRows },
    direct: { id: 'direct', name: 'Direct', color: '#64748b', rows: directRows }
  };
}

function formatMoney(x) {
  return '₺' + Math.round(x).toLocaleString('tr-TR');
}

// Smooth response curve for a channel using its (a,b) hyperparameters
function predictRevenue(channel, spend) {
  const { a, b } = channel;
  return a * (1 - Math.exp(-b * Math.max(0, spend)));
}

function marginalROAS(channel, spend, step = 1000) {
  const r1 = predictRevenue(channel, spend);
  const r2 = predictRevenue(channel, spend + step);
  return (r2 - r1) / step; // incremental revenue per 1₺
}

function greedyAllocate(channels, currentSpends, extraBudget = 10000, step = 1000, minMroas = 0) {
  const alloc = Object.fromEntries(channels.map(c => [c.id, 0]));
  const spends = { ...currentSpends };
  let remaining = extraBudget;
  let totalIncremental = 0;
  while (remaining >= step) {
    let bestId = null;
    let bestM = -Infinity;
    for (const ch of channels) {
      const m = marginalROAS(ch, spends[ch.id], step);
      if (m > bestM && m >= minMroas) {
        bestM = m;
        bestId = ch.id;
      }
    }
    if (!bestId || bestM <= 0) break;
    alloc[bestId] += step;
    spends[bestId] += step;
    totalIncremental += bestM * step;
    remaining -= step;
  }
  return { allocation: alloc, totalIncremental };
}

export default function ChannelMixReallocationDemo() {
  const data = useMemo(buildDummyData, []);
  const channels = useMemo(() => CHANNELS.map(c => data[c.id]), [data]);
  const baseline = useMemo(buildBaselineDummy, []);
  const [selectedId, setSelectedId] = useState(channels[0]?.id);
  const [extraBudget, setExtraBudget] = useState(10000);
  const [step, setStep] = useState(1000);
  const [minMroas, setMinMroas] = useState(0);
  const [includeBaseline, setIncludeBaseline] = useState(true);
  const [spilloverOn, setSpilloverOn] = useState(true);
  const [spilloverShare, setSpilloverShare] = useState(0.15); // portion of paid incremental that also appears as organic/direct

  const latestSpends = useMemo(() => {
    const map = {};
    channels.forEach(ch => {
      const last = ch.rows[ch.rows.length - 1];
      map[ch.id] = last?.spend || 0;
    });
    return map;
  }, [channels]);

  const latestRevenues = useMemo(() => {
    const map = {};
    channels.forEach(ch => {
      const last = ch.rows[ch.rows.length - 1];
      map[ch.id] = last?.revenue || 0;
    });
    return map;
  }, [channels]);

  const latestBaselineRevenue = useMemo(() => {
    const org = baseline.organic.rows[baseline.organic.rows.length - 1]?.revenue || 0;
    const dir = baseline.direct.rows[baseline.direct.rows.length - 1]?.revenue || 0;
    return { org, dir, total: org + dir };
  }, [baseline]);

  const recommendation = useMemo(() => {
    return greedyAllocate(channels, latestSpends, Number(extraBudget) || 0, Number(step) || 1000, Number(minMroas) || 0);
  }, [channels, latestSpends, extraBudget, step, minMroas]);

  const selected = channels.find(c => c.id === selectedId) || channels[0];
  const currentSpend = latestSpends[selected.id] || 0;
  const proposedSpend = currentSpend + (recommendation.allocation[selected.id] || 0);

  const curveData = useMemo(() => {
    const maxSpend = Math.max(1, currentSpend * 2 + 20000);
    const pts = [];
    const ticks = 40;
    for (let i = 0; i <= ticks; i++) {
      const s = (maxSpend * i) / ticks;
      pts.push({ spend: Math.round(s), revenue: Math.round(predictRevenue(selected, s)) });
    }
    return pts;
  }, [selected, currentSpend]);

  const totalCurrent = Object.values(latestSpends).reduce((a, b) => a + b, 0);
  const totalCurrentRevPaid = Object.values(latestRevenues).reduce((a, b) => a + b, 0);
  const baselineCurrent = latestBaselineRevenue.total;
  const totalCurrentRev = includeBaseline ? (totalCurrentRevPaid + baselineCurrent) : totalCurrentRevPaid;
  const totalProposed = totalCurrent + (Number(extraBudget) || 0);
  const paidIncremental = Math.round(recommendation.totalIncremental);
  const spilloverIncremental = spilloverOn ? Math.round(paidIncremental * Number(spilloverShare)) : 0;
  const totalIncremental = includeBaseline ? (paidIncremental + spilloverIncremental) : paidIncremental;
  const proposedTotalRevenue = totalCurrentRev + totalIncremental;

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Channel Mix & Budget Reallocation (Demo)</h1>
            <p className="text-sm text-gray-500 mt-1">Six‑month dummy data with diminishing‑returns curves and a +budget allocator</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <span className="text-xs text-gray-500">Extra Budget</span>
            <input
              type="number"
              className="w-28 border border-gray-200 rounded px-2 py-1 text-sm"
              value={extraBudget}
              onChange={(e) => setExtraBudget(e.target.value)}
              step={1000}
              min={0}
            />
            <span className="text-xs text-gray-500">₺</span>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <span className="text-xs text-gray-500">Step</span>
            <select className="border border-gray-200 rounded px-2 py-1 text-sm" value={step} onChange={(e) => setStep(e.target.value)}>
              <option value={500}>₺500</option>
              <option value={1000}>₺1,000</option>
              <option value={2000}>₺2,000</option>
            </select>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <span className="text-xs text-gray-500">Min MROAS</span>
            <input
              type="number"
              className="w-20 border border-gray-200 rounded px-2 py-1 text-sm"
              value={minMroas}
              onChange={(e) => setMinMroas(e.target.value)}
              step="0.1"
              min="0"
            />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" className="mr-1" checked={includeBaseline} onChange={(e)=>setIncludeBaseline(e.target.checked)} />
              Include Baseline (Organic + Direct)
            </label>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm p-2 border border-gray-200">
            <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" className="mr-1" checked={spilloverOn} onChange={(e)=>setSpilloverOn(e.target.checked)} />
              Spillover
            </label>
            <span className="text-xs text-gray-500">Share</span>
            <input
              type="number"
              className="w-16 border border-gray-200 rounded px-2 py-1 text-xs"
              value={spilloverShare}
              onChange={(e)=>setSpilloverShare(e.target.value)}
              step="0.05"
              min="0"
              max="1"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Total Current Spend</div>
          <div className="text-2xl font-bold text-gray-800">{formatMoney(totalCurrent)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Proposed Spend</div>
          <div className="text-2xl font-bold text-gray-800">{formatMoney(totalProposed)}</div>
          <div className="text-xs text-green-600 mt-1 flex items-center"><ArrowUpRight className="w-3 h-3 mr-1"/>+{formatMoney(extraBudget)}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Expected Incremental Revenue {includeBaseline && spilloverOn ? '(incl. spillover)' : ''}</div>
          <div className="text-2xl font-bold text-gray-800">{formatMoney(totalIncremental)}</div>
          {includeBaseline && spilloverOn ? <div className="text-[11px] text-gray-500 mt-1">Paid: {formatMoney(paidIncremental)} · Spillover: {formatMoney(spilloverIncremental)}</div> : null}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-1">Total Revenue (Current → Proposed)</div>
          <div className="text-2xl font-bold text-gray-800">{formatMoney(proposedTotalRevenue)}</div>
          <div className="text-xs text-green-600 mt-1">+{formatMoney(totalIncremental)} vs current {includeBaseline ? `(includes baseline ${formatMoney(baselineCurrent)})` : '(paid only)'}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left: Channels & Allocation */}
        <div className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800 text-sm">Allocation Recommendation</h3>
            <div className="flex items-center text-xs text-gray-500"><Info className="w-3.5 h-3.5 mr-1"/>Greedy allocator using marginal ROAS</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-gray-500">
                  <th className="text-left font-medium py-2 pr-4">Channel</th>
                  <th className="text-right font-medium py-2 pr-4">Current Spend</th>
                  <th className="text-right font-medium py-2 pr-4">Current Revenue</th>
                  <th className="text-right font-medium py-2 pr-4">Current ROAS</th>
                  <th className="text-right font-medium py-2 pr-4">+Budget</th>
                  <th className="text-right font-medium py-2 pr-4">New Spend</th>
                  <th className="text-right font-medium py-2 pr-4">Expected ΔRevenue</th>
                  <th className="text-right font-medium py-2">New ROAS</th>
                </tr>
              </thead>
              <tbody>
                {channels.map(ch => {
                  const alloc = recommendation.allocation[ch.id] || 0;
                  const curS = latestSpends[ch.id];
                  const curR = latestRevenues[ch.id];
                  const newS = curS + alloc;
                  const newR = Math.round(predictRevenue(ch, newS));
                  const dR = Math.max(0, newR - curR);
                  const roas = curS > 0 ? (curR / curS) : 0;
                  const newRoas = newS > 0 ? (newR / newS) : 0;
                  return (
                    <tr key={ch.id} className="border-t border-gray-100">
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ch.color }} />
                          <button className={`text-gray-800 hover:underline text-left`} onClick={() => setSelectedId(ch.id)}>{ch.name}</button>
                          {selectedId === ch.id && <span className="text-[10px] text-blue-600 ml-1">(selected)</span>}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right text-gray-800">{formatMoney(curS)}</td>
                      <td className="py-2 pr-4 text-right text-gray-800">{formatMoney(curR)}</td>
                      <td className="py-2 pr-4 text-right">
                        <span className={`${roas >= 1 ? 'text-green-600' : 'text-gray-600'}`}>{roas.toFixed(2)}x</span>
                      </td>
                      <td className="py-2 pr-4 text-right text-blue-700 font-medium">{alloc > 0 ? `+${formatMoney(alloc)}` : '—'}</td>
                      <td className="py-2 pr-4 text-right text-gray-800">{formatMoney(newS)}</td>
                      <td className="py-2 pr-4 text-right">
                        <span className={`${dR > 0 ? 'text-green-600' : 'text-gray-600'}`}>{dR > 0 ? `+${formatMoney(dR)}` : '—'}</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className={`${newRoas >= roas ? 'text-green-600' : 'text-red-600'}`}>{newRoas.toFixed(2)}x</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Response Curve for selected channel */}
        <div className="col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800 text-sm">Response Curve — {selected.name}</h3>
            <SlidersHorizontal className="w-4 h-4 text-gray-400" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={curveData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="spend" tickFormatter={(v) => `₺${Math.round(v).toLocaleString('tr-TR')}`} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tickFormatter={(v) => `₺${Math.round(v).toLocaleString('tr-TR')}`} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [`₺${Math.round(value).toLocaleString('tr-TR')}`, name === 'revenue' ? 'Revenue' : 'Spend']}
                  labelFormatter={(label) => `Spend: ₺${Math.round(label).toLocaleString('tr-TR')}`}
                />
                <Legend />
                <ReferenceLine x={currentSpend} stroke="#0ea5e9" strokeDasharray="3 3" label={{ value: 'Current', position: 'insideTopRight', fill: '#0ea5e9', fontSize: 11 }} />
                <ReferenceLine x={proposedSpend} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Proposed', position: 'insideTopRight', fill: '#10b981', fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" stroke={selected.color} strokeWidth={2} dot={false} name="Revenue" />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
            <div className="border border-gray-200 rounded-lg p-2">
              <div className="text-gray-500">Current</div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Spend</span>
                <span className="font-semibold text-gray-800">{formatMoney(currentSpend)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Revenue</span>
                <span className="font-semibold text-gray-800">{formatMoney(latestRevenues[selected.id] || 0)}</span>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-2">
              <div className="text-gray-500">Proposed</div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Spend</span>
                <span className="font-semibold text-gray-800">{formatMoney(proposedSpend)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Revenue</span>
                <span className="font-semibold text-green-700">~{formatMoney(predictRevenue(selected, proposedSpend))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">6‑Month Channel Trends</h3>
          <div className="text-xs text-gray-500 flex items-center"><DollarSign className="w-3.5 h-3.5 mr-1"/>Spend & Revenue by month (paid), baseline shown below</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium py-2 pr-4">Channel</th>
                {MONTHS.map(m => (
                  <th key={m} className="text-right font-medium py-2 pr-4">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {channels.map(ch => (
                <tr key={ch.id} className="border-t border-gray-100">
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ch.color }} />
                      <span className="text-gray-800">{ch.name}</span>
                    </div>
                  </td>
                  {ch.rows.map(r => (
                    <td key={r.month} className="py-2 pr-4 text-right">
                      <div className="text-gray-800">{formatMoney(r.revenue)}</div>
                      <div className="text-[10px] text-gray-500">Spend {formatMoney(r.spend)}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Baseline (Organic + Direct) Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-800 text-sm">Baseline Trends — Organic & Direct</h3>
          <div className="text-xs text-gray-500">Used as control (not allocated). {spilloverOn ? `Spillover share: ${(Number(spilloverShare)*100).toFixed(0)}%` : 'Spillover off'}.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium py-2 pr-4">Source</th>
                {MONTHS.map(m => (
                  <th key={m} className="text-right font-medium py-2 pr-4">{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[baseline.organic, baseline.direct].map(src => (
                <tr key={src.id} className="border-t border-gray-100">
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: src.color }} />
                      <span className="text-gray-800">{src.name}</span>
                    </div>
                  </td>
                  {src.rows.map(r => (
                    <td key={r.month} className="py-2 pr-4 text-right">
                      <div className="text-gray-800">{formatMoney(r.revenue)}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


